// Persisted user preferences (one-time onboarding).
import type { Preference } from "@/utils/scoring";
import type { TravelMode } from "@/api/routing";

const KEY = "pleasant.profile.v2";
const LEGACY_KEY = "pleasant.profile.v1";

/** Top-level "what kind of pleasant route do you want?" */
export type PleasantnessProfile =
  | "nature" | "water" | "historic" | "quiet" | "lively";

/** How much extra time the user will tolerate for a nicer route. */
export type DetourTolerance = "fast" | "balanced" | "scenic";

export const PROFILE_LABELS: Record<PleasantnessProfile, string> = {
  nature: "Nature-heavy",
  water: "Water views",
  historic: "Historic / architectural",
  quiet: "Quiet / low-stress",
  lively: "Lively / interesting",
};

export const PROFILE_BLURBS: Record<PleasantnessProfile, string> = {
  nature: "Parks, gardens, trees, greenways.",
  water: "Coast, rivers, lakes, harborfronts.",
  historic: "Old streets, landmarks, notable buildings.",
  quiet: "Small streets, low speeds, away from arterials.",
  lively: "Cafés, plazas, shops, cultural places.",
};

// Labels: Direct / Pleasant / Scenic — three brand-anchored options that
// place Pleasant in the middle as the obvious starting point. The
// matching blurbs stay concrete so the user knows what they're picking
// in real terms, but the labels themselves match the rest of the app's
// vocabulary (Pleasant route, Direct route).
export const TOLERANCE_LABELS: Record<DetourTolerance, string> = {
  fast: "Fast",
  balanced: "Pleasant",
  scenic: "Scenic",
};

export const TOLERANCE_BLURBS: Record<DetourTolerance, string> = {
  fast: "Stays on the fastest route — but picks the prettiest version of it.",
  balanced: "A few minutes longer than the fastest route, for quieter streets and prettier scenery. Where Pleasant Routes shines.",
  scenic: "Up to 80% longer when the detour is worth it. For trips where the journey IS the point.",
};

/** Multiplier on fastest-route duration. Drives both candidate generation
 *  and the gating step in classifyRoutes. */
export const TOLERANCE_MULTIPLIER: Record<DetourTolerance, number> = {
  fast: 1.0,
  balanced: 1.15,
  // Scenic gets a generous detour budget — up to 80% longer than the
  // fastest route — so it can genuinely swing through parks, waterfronts,
  // and backroads instead of feeling like a slightly-quieter Balanced.
  scenic: 1.80,
};

export interface UserProfile {
  loves: Preference[];
  mode: TravelMode;
  prioritizeCommunity?: boolean;
  /** New: which kind of pleasant route the user prefers. */
  pleasantnessProfile: PleasantnessProfile;
  /** New: how much extra time they'll accept. */
  detourTolerance: DetourTolerance;
  completedAt: string;
}

/** Best-effort: turn a legacy `loves` array into a default profile. */
function inferProfile(loves: Preference[]): PleasantnessProfile {
  if (loves.includes("scenic")) return "water";
  if (loves.includes("historic") || loves.includes("monuments") || loves.includes("architecture")) return "historic";
  if (loves.includes("quiet") || loves.includes("no-traffic")) return "quiet";
  if (loves.includes("coffee") || loves.includes("shops") || loves.includes("lively-streets")) return "lively";
  return "nature";
}

export function loadProfile(): UserProfile | null {
  try {
    const raw = localStorage.getItem(KEY) ?? localStorage.getItem(LEGACY_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!Array.isArray(p.loves) || !p.mode) return null;
    // Migrate v1 → v2 in place.
    const profile: UserProfile = {
      loves: p.loves,
      mode: p.mode,
      prioritizeCommunity: p.prioritizeCommunity ?? true,
      pleasantnessProfile: p.pleasantnessProfile ?? inferProfile(p.loves),
      detourTolerance: p.detourTolerance ?? "balanced",
      completedAt: p.completedAt ?? new Date().toISOString(),
    };
    return profile;
  } catch {
    return null;
  }
}

/** A neutral default profile so the app can open straight to the route search
 *  without the onboarding flow. Driving + balanced ("Pleasant") detour budget;
 *  preferences are no longer weighted in scoring, so the pleasantnessProfile is
 *  just a sensible default the user can change later in Settings. */
export function defaultProfile(): UserProfile {
  return {
    loves: [],
    mode: "driving",
    prioritizeCommunity: true,
    pleasantnessProfile: "nature",
    detourTolerance: "balanced",
    completedAt: new Date().toISOString(),
  };
}

export function saveProfile(p: Omit<UserProfile, "completedAt">): UserProfile {
  const full: UserProfile = { ...p, completedAt: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(full));
  } catch {
    /* ignore quota */
  }
  return full;
}

export function clearProfile() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(LEGACY_KEY);
  } catch { /* ignore */ }
}
